import { useEffect, useRef, useState } from 'react';

import {
  fetchInvestorProfile,
  fetchInvestorProperties,
  saveInvestorSnapshot,
} from 'data/investors/investorProfileService';

// Loads the Investor Profile modal's real backend data (ATAI-109 profile
// snapshot + ATAI-169 owned/sold properties) for one investor, alongside
// `Profile/index.js`'s `investor` prop rather than replacing it — the modal
// already renders instantly from the search-result object it was opened
// with, so this only ever *adds* richer data as each request resolves, the
// same "resolves or it throws, never leaves the UI hanging" spirit as
// data/contacts/psCoreActions.js. A failed request logs a warning and leaves
// its own piece in an error/empty state rather than blocking the rest.
//
// Saves the search-result investor via save-snapshot before fetching its
// profile, on purpose — keep it (ATAI-182):
//   1. After a Skip Trace or a manual contact edit, this modal re-reads the
//      profile (refetchProfile). For an investor that isn't in any list, the
//      snapshot written here is the only one there is — without it that
//      re-read 404s and the new contacts don't show until the modal reopens.
//   2. The profile then always matches the card the user just clicked.
// It's safe: since ATAI-172 the search result carries the full metric set,
// so this never thins out a stored snapshot, and since ATAI-182 the server
// stores every snapshot without contacts (phone/email stay per-user in
// propstream-be). Keeping saved investors fresh is the server's job now — it
// refreshes expired snapshots on profile read and from search results.
//
// Properties are one `ownership=any` call (current + previous merged
// server-side), not two — split back into acquisition/disposition here by
// each row's own `saleInfo` (null for owned, populated for sold), same
// signal the backend uses internally. Halves the requests this modal makes
// without losing the per-tab split History/PropertyTable need.
const useInvestorProfileData = (investor, onProfileRefetched) => {
  // Latest callback without making it an effect dependency (it's a fresh
  // closure every render, and re-running the fetch effect for that would
  // re-materialize and re-fetch for nothing).
  const onProfileRefetchedRef = useRef(onProfileRefetched);
  onProfileRefetchedRef.current = onProfileRefetched;

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const [acquisitionProperties, setAcquisitionProperties] = useState([]);
  const [dispositionProperties, setDispositionProperties] = useState([]);
  const [propertiesTotal, setPropertiesTotal] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  const investorId = investor?.id;
  // Bumped to force the effect below to re-run on demand (see refetchProfile)
  // -- investorId alone never changes after a Skip Trace, so that's not
  // something the effect's own dependency array can key on.
  const [refetchToken, setRefetchToken] = useState(0);
  const refetchProfile = () => setRefetchToken((t) => t + 1);

  useEffect(() => {
    if (!investorId) return undefined;

    let cancelled = false;

    // A manual refetch (refetchToken > 0, e.g. right after Skip Trace) only
    // needs the profile/contacts re-read -- not a re-materialize (nothing
    // about the investor's own snapshot changed) and not the properties
    // tables (unaffected by skip trace), so it skips resetting those and
    // skips the save-snapshot call the initial load does.
    const isRefetch = refetchToken > 0;

    setProfileError(null);
    setProfileLoading(true);
    if (!isRefetch) {
      setProfile(null);
      setAcquisitionProperties([]);
      setDispositionProperties([]);
      setPropertiesTotal(null);
      setPropertiesLoading(true);
    }

    // ATAI-180: opening this modal from the Contacts Investor List only ever
    // has a thin `{id}` object (no metrics/mailingAddresses/contacts) — never
    // re-save with that, or it overwrites a real, richer snapshot with an
    // almost-empty one (save-snapshot is a full replace). Only re-materialize
    // when the caller actually has the fuller search-result shape.
    const hasRichPayload = Boolean(investor?.metrics || investor?.mailingAddresses || investor?.contacts);

    const run = async () => {
      if (hasRichPayload && !isRefetch) {
        try {
          await saveInvestorSnapshot(investorId, investor);
        } catch (error) {
          // Not fatal — the fetches below just 404/fall back to the
          // search-result data already on screen, same as if this investor
          // already had no snapshot for some other reason.
          console.warn('Could not materialize investor snapshot:', error);
        }
      }

      if (cancelled) return;

      fetchInvestorProfile(investorId)
        .then(({ data }) => {
          if (cancelled) return;
          setProfile(data);
          // Only a refetch (contact edit / skip trace): the initial load is
          // the same data the card already showed.
          if (isRefetch) onProfileRefetchedRef.current?.(data);
        })
        .catch((error) => {
          console.warn('Could not load investor profile:', error);
          if (!cancelled) setProfileError(error);
        })
        .finally(() => {
          if (!cancelled) setProfileLoading(false);
        });

      if (isRefetch) return;

      fetchInvestorProperties(investorId, 'current')
        .then(({ data }) => {
          if (cancelled) return;
          const properties = data?.properties || [];
          setAcquisitionProperties(properties.filter((property) => !property.saleInfo));
          setDispositionProperties(properties.filter((property) => property.saleInfo));
          setPropertiesTotal(data?.totalCount ?? null);
        })
        .catch((error) => {
          console.warn('Could not load investor properties:', error);
        })
        .finally(() => {
          if (!cancelled) setPropertiesLoading(false);
        });
    };

    run();

    return () => {
      cancelled = true;
    };
    // Keyed on the id (not the object reference: the popup's investor prop
    // is fixed for the modal's lifetime, and re-running this on every parent
    // re-render would re-materialize + re-fetch for no reason) plus
    // refetchToken, so refetchProfile() can force one more pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId, refetchToken]);

  // ATAI-210: `profileLoading` only flips to true inside the effect, i.e. after
  // the first render has already painted -- so opened by id from My Contacts
  // (a thin {id, contactId} object, no name), the header flashed "Unknown
  // investor" plus an unsaved bookmark for a frame before the spinner took
  // over. An investor with an id and neither a profile nor an error yet is
  // still loading, from the very first render.
  const awaitingProfile = Boolean(investorId) && !profile && !profileError;

  return {
    profile,
    profileLoading: profileLoading || awaitingProfile,
    profileError,
    acquisitionProperties,
    dispositionProperties,
    propertiesTotal,
    propertiesLoading,
    refetchProfile,
  };
};

export default useInvestorProfileData;
